import { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, Alert, App as AntApp } from 'antd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAcceptInviteMutation } from './hooks';
import { useAuthStore } from '@starling/core/auth/store';
import { ApiError } from '@starling/core/api/errors';

const { Title, Text } = Typography;

interface AcceptInviteValues {
  name: string;
  password: string;
  password_confirm: string;
}

/**
 * Public-страница `/accept-invite?token=...` — финализация приглашения сотрудника.
 *
 * Поток (см. ТЗ M4.2):
 *   1. Парсим `token` из query string.
 *   2. Если token отсутствует — показываем error-toast и редиректим на /login.
 *   3. Форма name + password + confirm; password ≥8 (требование backend).
 *   4. Submit → POST /auth/accept-invite {token, name, password}.
 *   5. Успех → auth-store сам подхватит TokenPair (через onSuccess в хуке),
 *      редиректим в /customers — там сотрудник работает с привязками клиентов.
 *   6. Ошибки `invitation_*` маппим в локализованные сообщения внутри
 *      `errors.<code>` namespace (см. i18n).
 */
export function AcceptInvitePage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { message } = AntApp.useApp();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<AcceptInviteValues>();
  const token = searchParams.get('token');

  const mutation = useAcceptInviteMutation();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  // Режим привязки существующего аккаунта. Включается реактивно, когда submit
  // вернул 409 `email_already_registered` (см. design-brief-invite-existing-account.md).
  // `tenantName` — имя оптики из `error.details.tenant_name`; пустая строка, если
  // бэкенд по какой-то причине его не прислал (тогда показываем фолбэк-текст).
  const [linkMode, setLinkMode] = useState<{ tenantName: string } | null>(null);
  const isLinkMode = linkMode !== null;

  // Если token нет в URL — ссылка битая. Уводим на /login с тостом.
  // useEffect, а не ранний return, чтобы не нарушить порядок хуков.
  useEffect(() => {
    if (!token) {
      void message.error(t('auth.accept_invite.no_token'));
      navigate('/login', { replace: true });
    }
  }, [token, navigate, message, t]);

  // Если пользователь уже залогинен (например, после успешного accept'а с
  // setTokens) — уводим в /customers. Это же сработает, если он зашёл на
  // /accept-invite будучи уже авторизованным под другим аккаунтом.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/customers', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const onFinish = (values: AcceptInviteValues): void => {
    if (!token) return; // защита от race: useEffect уже редиректит, но TS того не знает.
    // В режиме привязки поле «Имя» размонтировано, поэтому в `values` его нет —
    // читаем сохранённое (preserve) значение из стора формы. Бэкенд его игнорирует
    // при reuse-аккаунте, но контракт требует поле в payload.
    const name = (values.name ?? form.getFieldValue('name') ?? '') as string;
    mutation.mutate(
      { token, name: name.trim(), password: values.password },
      {
        onSuccess: () => {
          // setTokens вызывает onSuccess в самом хуке; редирект случится через
          // эффект на isAuthenticated. Дублируем явно для надёжности (на случай,
          // если эффект ещё не успел отработать к моменту mutation.onSuccess).
          navigate('/customers', { replace: true });
        },
        onError: (err) => {
          if (
            err instanceof ApiError &&
            err.status === 409 &&
            err.code === 'email_already_registered'
          ) {
            // Email занят — не показываем generic-toast, а перестраиваем форму
            // в режим привязки. Имя оптики берём из details.tenant_name.
            const tenantName =
              typeof err.details?.tenant_name === 'string' ? err.details.tenant_name : '';
            setLinkMode({ tenantName });
            return;
          }
          if (err instanceof ApiError) {
            // Семантические коды контракта (D5 + identity/router.py).
            const codeKey = `auth.accept_invite.errors.${err.code}`;
            const localized = t(codeKey);
            // i18next в отсутствие ключа возвращает сам ключ — детектим это
            // и фолбэчим на generic, чтобы не показать «auth.accept_invite.errors.foo».
            if (localized !== codeKey) {
              void message.error(localized);
              return;
            }
          }
          void message.error(t('auth.accept_invite.errors.generic'));
        },
      },
    );
  };

  // Пока useEffect ещё не успел редиректнуть на /login — рендерим пустую
  // карточку, чтобы не моргнуть формой без токена.
  if (!token) {
    return <div style={{ minHeight: '100vh', background: 'var(--mist)' }} />;
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--mist)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 480 }} variant="borderless">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('auth.accept_invite.title')}
          </Title>
          <Text type="secondary">{t('auth.accept_invite.subtitle')}</Text>
        </div>
        {isLinkMode && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              linkMode.tenantName
                ? t('auth.accept_invite.link_mode.hint', { tenant: linkMode.tenantName })
                : t('auth.accept_invite.errors.email_already_registered')
            }
          />
        )}
        <Form<AcceptInviteValues>
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={onFinish}
          disabled={mutation.isPending}
        >
          {/*
            В режиме привязки поле «Имя» скрыто: имя игнорируется бэкендом при
            reuse-аккаунте. Form.Item размонтируется, но AntD (preserve=true по
            умолчанию) сохраняет введённое ранее значение — оно уйдёт в payload.
          */}
          {!isLinkMode && (
            <Form.Item
              label={t('auth.accept_invite.name')}
              name="name"
              rules={[
                { required: true, message: t('auth.accept_invite.name_required') },
                { max: 200 },
              ]}
            >
              <Input
                autoComplete="name"
                placeholder={t('auth.accept_invite.name_placeholder')}
                autoFocus
              />
            </Form.Item>
          )}
          <Form.Item
            label={
              isLinkMode
                ? t('auth.accept_invite.link_mode.password')
                : t('auth.accept_invite.password')
            }
            name="password"
            rules={
              isLinkMode
                ? [{ required: true, message: t('auth.accept_invite.password_required') }]
                : [
                    { required: true, message: t('auth.accept_invite.password_required') },
                    { min: 8, message: t('auth.accept_invite.password_min_length') },
                  ]
            }
          >
            <Input.Password autoComplete={isLinkMode ? 'current-password' : 'new-password'} />
          </Form.Item>
          {!isLinkMode && (
            <Form.Item
              label={t('auth.accept_invite.password_confirm')}
              name="password_confirm"
              dependencies={['password']}
              rules={[
                { required: true, message: t('auth.accept_invite.password_required') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('auth.accept_invite.password_mismatch')));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
          )}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={mutation.isPending}
              size="large"
            >
              {isLinkMode
                ? t('auth.accept_invite.link_mode.submit')
                : t('auth.accept_invite.submit')}
            </Button>
          </Form.Item>
        </Form>
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Text type="secondary">{t('auth.accept_invite.have_account')} </Text>
          <Link to="/login">{t('auth.accept_invite.login_link')}</Link>
        </div>
      </Card>
    </div>
  );
}
